import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { aiHelpers, api, type AIBlogGenerationPrompt, type AIGeneratedBlogPost } from '@/lib/api';
import {
  Sparkles,
  Loader2,
  X,
  Wand2,
  FileText,
  Image,
  Check,
  ChevronDown,
  ChevronUp,
  Target,
  Tags,
  BarChart3,
  HelpCircle,
  Copy,
  ArrowRight,
  Star,
  Lightbulb,
  Palette,
  ImagePlus,
} from 'lucide-react';

interface AIBlogGeneratorProps {
  onGenerated: (post: AIGeneratedBlogPost, selectedImage?: string) => void;
  onClose: () => void;
  projectIndustry?: string; // Pre-populated from project settings
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Formal, authoritative tone' },
  { value: 'casual', label: 'Casual', description: 'Friendly, conversational style' },
  { value: 'educational', label: 'Educational', description: 'Informative, teaching-focused' },
  { value: 'entertaining', label: 'Entertaining', description: 'Fun, engaging content' },
  { value: 'inspirational', label: 'Inspirational', description: 'Motivating, uplifting tone' },
] as const;

const WORD_COUNT_OPTIONS = [
  { value: 500, label: 'Short (~500 words)', description: '2-3 min read' },
  { value: 1000, label: 'Medium (~1000 words)', description: '4-5 min read' },
  { value: 1500, label: 'Standard (~1500 words)', description: '6-7 min read' },
  { value: 2500, label: 'Long (~2500 words)', description: '10-12 min read' },
];

// Business goal options
const GOAL_OPTIONS = {
  traffic: {
    label: 'Drive Traffic',
    description: 'SEO-optimized content to attract visitors',
    icon: '📈',
  },
  leads: {
    label: 'Generate Leads',
    description: 'Convert readers into customers',
    icon: '💰',
  },
  authority: {
    label: 'Build Authority',
    description: 'Establish thought leadership',
    icon: '👑',
  },
  awareness: {
    label: 'Spread Awareness',
    description: 'Educate your audience',
    icon: '💡',
  },
} as const;

// Popular industry suggestions for quick selection
const INDUSTRY_SUGGESTIONS = [
  'E-commerce & Retail',
  'SaaS & Technology',
  'Healthcare & Wellness',
  'Education & EdTech',
  'Real Estate',
  'Finance & FinTech',
  'Marketing & Advertising',
  'Travel & Hospitality',
];

const AUDIENCE_SUGGESTIONS = [
  'Small business owners and entrepreneurs',
  'Marketing professionals and CMOs',
  'Tech-savvy millennials and Gen Z',
  'Parents and families',
  'Healthcare professionals',
  'Educators and teachers',
  'E-commerce store owners',
  'Startup founders',
];

export function AIBlogGenerator({ onGenerated, onClose, projectIndustry }: AIBlogGeneratorProps) {
  const [step, setStep] = useState<'prompt' | 'generating' | 'preview'>('prompt');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<keyof typeof GOAL_OPTIONS | null>(null);
  const [industry, setIndustry] = useState(projectIndustry || '');

  // Dynamic topic suggestions state
  const [suggestedTopics, setSuggestedTopics] = useState<Array<{ title: string; reason: string }>>(
    []
  );
  const [loadingTopics, setLoadingTopics] = useState(false);

  // Form state
  const [topic, setTopic] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [tone, setTone] = useState<AIBlogGenerationPrompt['tone']>('professional');
  const [keywords, setKeywords] = useState('');
  const [wordCount, setWordCount] = useState(1500);
  const [includeStatistics, setIncludeStatistics] = useState(true);
  const [includeFAQ, setIncludeFAQ] = useState(true);

  // Keyword suggestion state
  const [suggestingKeywords, setSuggestingKeywords] = useState(false);

  // Featured image generation state
  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageStyle, setImageStyle] = useState<'photo' | 'illustration' | 'digital-art' | '3d'>(
    'photo'
  );
  const [generatingImagePrompts, setGeneratingImagePrompts] = useState(false);
  const [suggestedImagePrompts, setSuggestedImagePrompts] = useState<string[]>([]);
  const [generatingImage, setGeneratingImage] = useState<string | null>(null); // Track which prompt is generating

  // Generated content state
  const [generatedPost, setGeneratedPost] = useState<AIGeneratedBlogPost | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [rating, setRating] = useState<number>(0);
  const [savingTraining, setSavingTraining] = useState(false);

  // Function to fetch AI-generated topic suggestions
  const handleSuggestTopics = async () => {
    if (!industry.trim() || !selectedGoal) {
      toast.error('Please enter your industry and select a goal first');
      return;
    }

    setLoadingTopics(true);
    setSuggestedTopics([]);

    try {
      const response = await aiHelpers.suggestTopics(
        industry.trim(),
        selectedGoal,
        targetAudience.trim() || undefined
      );

      if (response.data.success && response.data.data?.topics) {
        setSuggestedTopics(response.data.data.topics);
        toast.success('Topic suggestions generated!');
      } else {
        toast.error('Failed to generate topics');
      }
    } catch (error) {
      console.error('Topic suggestion error:', error);
      toast.error('Failed to generate topic suggestions');
    }

    setLoadingTopics(false);
  };

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: (prompt: AIBlogGenerationPrompt) => aiHelpers.generateBlogPost(prompt),
    onSuccess: (response) => {
      if (response.data.success && response.data.data) {
        setGeneratedPost(response.data.data);
        // Auto-select first image if available
        if (response.data.data.suggestedImages?.[0]) {
          setSelectedImage(response.data.data.suggestedImages[0].url);
        }
        setStep('preview');
        toast.success('Blog post generated successfully!');
      } else {
        toast.error(response.data.error?.message || 'Generation failed');
      }
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message || 'Failed to generate blog post');
      setStep('prompt');
    },
  });

  const handleGenerate = () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    setStep('generating');

    const prompt: AIBlogGenerationPrompt = {
      topic: topic.trim(),
      targetAudience: targetAudience.trim() || undefined,
      tone,
      keywords: keywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean),
      wordCount,
      includeStatistics,
      includeFAQ,
    };

    generateMutation.mutate(prompt);
  };

  const handleUseGenerated = async () => {
    if (generatedPost) {
      // Save as training data if rated
      if (rating > 0) {
        setSavingTraining(true);
        try {
          await api.post('/ai/training/examples', {
            prompt: topic,
            input: JSON.stringify({
              topic,
              targetAudience,
              tone,
              keywords: keywords
                .split(',')
                .map((k) => k.trim())
                .filter(Boolean),
              wordCount,
            }),
            output: JSON.stringify({
              title: generatedPost.title,
              content: generatedPost.content,
              excerpt: generatedPost.excerpt,
              tags: generatedPost.tags,
              seo: generatedPost.seo,
            }),
            type: 'blog_generation',
            rating,
            approved: true,
          });
          toast.success('Saved to training data for AI improvement!');
        } catch {
          // Silent fail - don't block the main action
          console.error('Failed to save training data');
        }
        setSavingTraining(false);
      }
      onGenerated(generatedPost, selectedImage || undefined);
    }
  };

  // Handle keyword suggestion
  const handleSuggestKeywords = async () => {
    if (!topic.trim()) return;

    setSuggestingKeywords(true);
    try {
      const response = await aiHelpers.suggestKeywords(topic.trim(), 8);
      if (response.data.success && response.data.data?.keywords) {
        const suggestedKeywords = response.data.data.keywords.join(', ');
        setKeywords(suggestedKeywords);
        toast.success('Keywords suggested successfully!');
        // Auto-expand advanced options to show keywords
        setShowAdvanced(true);
      } else {
        toast.error('Failed to suggest keywords');
      }
    } catch (error) {
      console.error('Keyword suggestion error:', error);
      toast.error('Failed to suggest keywords');
    }
    setSuggestingKeywords(false);
  };

  // Handle image prompt generation
  const handleGenerateImagePrompts = async () => {
    const promptText = imagePrompt.trim() || topic.trim();
    if (!promptText) return;

    setGeneratingImagePrompts(true);
    try {
      const response = await aiHelpers.generateImagePrompts(promptText, imageStyle);
      if (response.data.success && response.data.data?.prompts) {
        setSuggestedImagePrompts(response.data.data.prompts);
        toast.success('Image prompts generated!');
      } else {
        toast.error('Failed to generate image prompts');
      }
    } catch (error) {
      console.error('Image prompt generation error:', error);
      toast.error('Failed to generate image prompts');
    }
    setGeneratingImagePrompts(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-background relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="from-primary/10 to-primary/5 flex items-center justify-between border-b bg-gradient-to-r px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
              <Sparkles className="text-primary h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">AI Blog Generator</h2>
              <p className="text-muted-foreground text-sm">
                {step === 'prompt' && 'Describe what you want to write about'}
                {step === 'generating' && 'Creating your blog post...'}
                {step === 'preview' && 'Review your generated content'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-muted rounded-lg p-2 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[calc(90vh-140px)] overflow-y-auto p-6">
          {/* Step 1: Prompt Input */}
          {step === 'prompt' && (
            <div className="space-y-6">
              {/* Industry/Niche Input */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <BarChart3 className="h-4 w-4" />
                  What's your industry or niche? *
                </label>
                {projectIndustry && industry === projectIndustry && (
                  <p className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                    <span className="inline-block h-2 w-2 rounded-full bg-green-500"></span>
                    Pre-filled from project settings
                  </p>
                )}
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => {
                    setIndustry(e.target.value);
                    setSuggestedTopics([]); // Clear suggestions when industry changes
                  }}
                  placeholder="e.g., AR/VR for Education, E-commerce, SaaS, Healthcare..."
                  className="bg-background focus:ring-primary w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2"
                  autoFocus
                />
                {/* Industry quick-select chips */}
                <div className="flex flex-wrap gap-2">
                  {INDUSTRY_SUGGESTIONS.map((ind) => (
                    <button
                      key={ind}
                      type="button"
                      onClick={() => {
                        setIndustry(ind);
                        setSuggestedTopics([]);
                      }}
                      className={`rounded-full border px-3 py-1.5 text-xs transition-all ${
                        industry === ind
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'hover:border-primary/50 hover:bg-muted'
                      }`}
                    >
                      {ind}
                    </button>
                  ))}
                </div>
              </div>

              {/* Business Goal Selection */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Target className="h-4 w-4" />
                  What's your content goal?
                </label>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {(
                    Object.entries(GOAL_OPTIONS) as [
                      keyof typeof GOAL_OPTIONS,
                      (typeof GOAL_OPTIONS)[keyof typeof GOAL_OPTIONS],
                    ][]
                  ).map(([key, value]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setSelectedGoal(selectedGoal === key ? null : key);
                        setSuggestedTopics([]); // Clear suggestions when goal changes
                      }}
                      className={`rounded-xl border p-3 text-center transition-all ${
                        selectedGoal === key
                          ? 'border-primary bg-primary/10 ring-primary ring-2'
                          : 'hover:border-primary/50 hover:bg-muted'
                      }`}
                    >
                      <span className="mb-1 block text-2xl">{value.icon}</span>
                      <span className="block text-sm font-medium">{value.label}</span>
                      <span className="text-muted-foreground mt-0.5 block text-xs">
                        {value.description}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Get AI Topic Suggestions Button */}
              {industry.trim() && selectedGoal && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleSuggestTopics}
                    disabled={loadingTopics}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-3 text-sm font-medium text-white shadow-lg transition-all hover:from-violet-600 hover:to-purple-700 hover:shadow-xl disabled:opacity-50"
                  >
                    {loadingTopics ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {loadingTopics ? 'Researching Trends...' : 'Get AI Topic Suggestions'}
                  </button>
                </div>
              )}

              {/* Dynamic AI-Generated Topic Suggestions */}
              {suggestedTopics.length > 0 && (
                <div className="border-primary/20 bg-primary/5 space-y-3 rounded-xl border p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="flex items-center gap-2 text-sm font-medium">
                      <Sparkles className="h-4 w-4 text-violet-500" />
                      AI-Researched Topics for {industry}
                    </h4>
                    <span className="text-muted-foreground text-xs">Click to use</span>
                  </div>
                  <div className="grid gap-2">
                    {suggestedTopics.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setTopic(suggestion.title);
                          setSuggestedTopics([]);
                        }}
                        className="bg-background hover:bg-primary/10 hover:border-primary group flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-all"
                      >
                        <div className="flex w-full items-center gap-2">
                          <ArrowRight className="text-muted-foreground group-hover:text-primary h-4 w-4 flex-shrink-0" />
                          <span className="flex-1 text-sm font-medium">{suggestion.title}</span>
                        </div>
                        <span className="text-muted-foreground ml-6 text-xs">
                          {suggestion.reason}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="text-muted-foreground mt-2 text-xs">
                    ✨ These topics are based on current 2025 trends and optimized for your selected
                    goal
                  </p>
                </div>
              )}

              {/* Main Topic */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Wand2 className="h-4 w-4" />
                  What do you want to write about? *
                </label>
                <textarea
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., 10 Benefits of Augmented Reality in Early Childhood Education"
                  className="bg-background focus:ring-primary min-h-[100px] w-full rounded-xl border p-4 focus:outline-none focus:ring-2"
                  autoFocus
                />
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-xs">
                    Be specific! Include the angle, audience, or key points you want to cover.
                  </p>
                  {topic.trim() && (
                    <button
                      type="button"
                      onClick={handleSuggestKeywords}
                      disabled={suggestingKeywords}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1.5 text-xs font-medium text-white transition-all hover:from-amber-600 hover:to-orange-600 disabled:opacity-50"
                    >
                      {suggestingKeywords ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Lightbulb className="h-3 w-3" />
                      )}
                      Suggest SEO Keywords
                    </button>
                  )}
                </div>
              </div>

              {/* Target Audience with suggestions */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Target className="h-4 w-4" />
                  Target Audience
                </label>
                <input
                  type="text"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="e.g., Preschool teachers and early childhood educators"
                  className="bg-background focus:ring-primary w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2"
                />
                {/* Audience quick-select chips */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {AUDIENCE_SUGGESTIONS.slice(0, 4).map((audience) => (
                    <button
                      key={audience}
                      type="button"
                      onClick={() => setTargetAudience(audience)}
                      className={`rounded-full border px-3 py-1.5 text-xs transition-all ${
                        targetAudience === audience
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'hover:border-primary/50 hover:bg-muted'
                      }`}
                    >
                      {audience}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tone Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Writing Tone</label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                  {TONE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setTone(option.value)}
                      className={`rounded-xl border p-3 text-left transition-all ${
                        tone === option.value
                          ? 'border-primary bg-primary/5 ring-primary ring-2'
                          : 'hover:border-primary/50 hover:bg-muted'
                      }`}
                    >
                      <div className="text-sm font-medium">{option.label}</div>
                      <div className="text-muted-foreground text-xs">{option.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Advanced Options Toggle */}
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm font-medium transition-colors"
              >
                {showAdvanced ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Advanced Options
              </button>

              {/* Advanced Options */}
              {showAdvanced && (
                <div className="bg-muted/50 space-y-4 rounded-xl border p-4">
                  {/* Keywords */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Tags className="h-4 w-4" />
                      SEO Keywords
                    </label>
                    <input
                      type="text"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      placeholder="augmented reality, early education, preschool learning"
                      className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
                    />
                    <p className="text-muted-foreground text-xs">Separate keywords with commas</p>
                  </div>

                  {/* Word Count */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Article Length</label>
                    <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                      {WORD_COUNT_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setWordCount(option.value)}
                          className={`rounded-lg border p-2 text-center transition-all ${
                            wordCount === option.value
                              ? 'border-primary bg-primary/5'
                              : 'hover:bg-muted'
                          }`}
                        >
                          <div className="text-sm font-medium">{option.label}</div>
                          <div className="text-muted-foreground text-xs">{option.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Include Options */}
                  <div className="flex flex-wrap gap-4">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={includeStatistics}
                        onChange={(e) => setIncludeStatistics(e.target.checked)}
                        className="h-4 w-4 rounded"
                      />
                      <BarChart3 className="h-4 w-4" />
                      <span className="text-sm">Include statistics & data</span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={includeFAQ}
                        onChange={(e) => setIncludeFAQ(e.target.checked)}
                        className="h-4 w-4 rounded"
                      />
                      <HelpCircle className="h-4 w-4" />
                      <span className="text-sm">Include FAQ section</span>
                    </label>
                  </div>

                  {/* Featured Image Generation */}
                  <div className="space-y-3 border-t pt-4">
                    <button
                      type="button"
                      onClick={() => setShowImageGenerator(!showImageGenerator)}
                      className="hover:text-primary flex items-center gap-2 text-sm font-medium transition-colors"
                    >
                      <ImagePlus className="h-4 w-4" />
                      Generate Featured Image Prompts
                      {showImageGenerator ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>

                    {showImageGenerator && (
                      <div className="bg-background space-y-3 rounded-lg border p-3">
                        <div className="space-y-2">
                          <label className="text-muted-foreground text-xs font-medium">
                            Describe the image you want (or leave empty to use topic)
                          </label>
                          <input
                            type="text"
                            value={imagePrompt}
                            onChange={(e) => setImagePrompt(e.target.value)}
                            placeholder={
                              topic || 'e.g., Children using AR tablets in a colorful classroom'
                            }
                            className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-muted-foreground text-xs font-medium">
                            Image Style
                          </label>
                          <div className="grid grid-cols-4 gap-2">
                            {[
                              { value: 'photo', label: 'Photo', icon: '📷' },
                              { value: 'illustration', label: 'Illustration', icon: '🎨' },
                              { value: 'digital-art', label: 'Digital Art', icon: '✨' },
                              { value: '3d', label: '3D Render', icon: '🎮' },
                            ].map((style) => (
                              <button
                                key={style.value}
                                type="button"
                                onClick={() => setImageStyle(style.value as typeof imageStyle)}
                                className={`rounded-lg border p-2 text-center text-xs transition-all ${
                                  imageStyle === style.value
                                    ? 'border-primary bg-primary/10'
                                    : 'hover:bg-muted'
                                }`}
                              >
                                <span className="block text-lg">{style.icon}</span>
                                {style.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={handleGenerateImagePrompts}
                          disabled={
                            generatingImagePrompts || (!imagePrompt.trim() && !topic.trim())
                          }
                          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 px-3 py-2 text-sm font-medium text-white transition-all hover:from-violet-600 hover:to-purple-700 disabled:opacity-50"
                        >
                          {generatingImagePrompts ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Palette className="h-4 w-4" />
                          )}
                          Generate Image Prompts
                        </button>

                        {suggestedImagePrompts.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-muted-foreground text-xs font-medium">
                              Click "Generate" to create with DALL-E, or copy for other tools:
                            </p>
                            {suggestedImagePrompts.map((prompt, index) => (
                              <div
                                key={index}
                                className="bg-muted/30 group flex items-start gap-2 rounded-lg border p-2"
                              >
                                <p className="flex-1 text-xs">{prompt}</p>
                                <div className="flex gap-1">
                                  <button
                                    type="button"
                                    disabled={generatingImage !== null}
                                    onClick={async () => {
                                      setGeneratingImage(prompt);
                                      try {
                                        const response = await aiHelpers.createImage(
                                          prompt,
                                          '1792x1024',
                                          'standard'
                                        );
                                        if (response.data.success && response.data.data?.url) {
                                          setSelectedImage(response.data.data.url);
                                          toast.success('Image generated!');
                                          setSuggestedImagePrompts([]); // Clear prompts after success
                                        } else {
                                          toast.error('Failed to generate image');
                                        }
                                      } catch (err) {
                                        console.error('Image generation error:', err);
                                        toast.error('Failed to generate image');
                                      }
                                      setGeneratingImage(null);
                                    }}
                                    className="hover:bg-primary/10 text-primary rounded p-1 text-xs font-medium transition-colors disabled:opacity-50"
                                  >
                                    {generatingImage === prompt ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <ImagePlus className="h-3 w-3" />
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard.writeText(prompt);
                                      toast.success('Prompt copied!');
                                    }}
                                    className="hover:bg-muted rounded p-1 opacity-0 transition-opacity group-hover:opacity-100"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            <p className="text-muted-foreground text-xs">
                              💡 Click the image icon to generate with DALL-E, or copy for
                              Midjourney/Stable Diffusion
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Generating */}
          {step === 'generating' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative">
                <div className="bg-primary/10 flex h-24 w-24 items-center justify-center rounded-full">
                  <Sparkles className="text-primary h-12 w-12 animate-pulse" />
                </div>
                <Loader2 className="text-primary absolute -inset-2 h-28 w-28 animate-spin" />
              </div>
              <h3 className="mt-6 text-xl font-semibold">Generating Your Blog Post</h3>
              <p className="text-muted-foreground mt-2 text-center">
                Our AI is researching and writing your content...
                <br />
                This may take 30-60 seconds.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {['Researching topic', 'Writing content', 'Optimizing SEO', 'Adding images'].map(
                  (task, i) => (
                    <span
                      key={task}
                      className="bg-muted animate-pulse rounded-full px-3 py-1 text-xs"
                      style={{ animationDelay: `${i * 0.5}s` }}
                    >
                      {task}
                    </span>
                  )
                )}
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && generatedPost && (
            <div className="space-y-6">
              {/* Generated Title & Excerpt */}
              <div className="rounded-xl border p-4">
                <div className="mb-2 flex items-center gap-2">
                  <FileText className="text-primary h-5 w-5" />
                  <span className="font-medium">Generated Title</span>
                </div>
                <h3 className="text-xl font-bold">{generatedPost.title}</h3>
                <p className="text-muted-foreground mt-2 text-sm">{generatedPost.excerpt}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {generatedPost.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Image Selection */}
              {generatedPost.suggestedImages && generatedPost.suggestedImages.length > 0 && (
                <div className="rounded-xl border p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <Image className="text-primary h-5 w-5" />
                    <span className="font-medium">Select Featured Image</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                    {generatedPost.suggestedImages.map((img) => (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => setSelectedImage(img.url)}
                        className={`group relative aspect-video overflow-hidden rounded-lg border-2 transition-all ${
                          selectedImage === img.url
                            ? 'border-primary ring-primary ring-2'
                            : 'hover:border-primary/50 border-transparent'
                        }`}
                      >
                        <img
                          src={img.thumbnailUrl}
                          alt={img.alt}
                          className="h-full w-full object-cover"
                        />
                        {selectedImage === img.url && (
                          <div className="bg-primary absolute inset-0 flex items-center justify-center bg-opacity-20">
                            <Check className="h-6 w-6 text-white drop-shadow-lg" />
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <p className="truncate text-xs text-white">{img.photographer}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Content Preview */}
              <div className="rounded-xl border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="text-primary h-5 w-5" />
                    <span className="font-medium">Content Preview</span>
                  </div>
                  <span className="text-muted-foreground text-sm">
                    ~{generatedPost.estimatedReadTime} min read
                  </span>
                </div>
                <div
                  className="prose prose-sm dark:prose-invert max-h-[300px] overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: generatedPost.content }}
                />
              </div>

              {/* SEO Info */}
              <div className="bg-muted/50 rounded-xl border p-4">
                <div className="mb-2 font-medium">SEO Optimization</div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Meta Title:</span>{' '}
                    {generatedPost.seo.metaTitle}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Meta Description:</span>{' '}
                    {generatedPost.seo.metaDescription}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between border-t px-6 py-4">
          {step === 'prompt' && (
            <>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={!topic.trim()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-xl px-6 py-2.5 font-medium transition-colors disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
                Generate Blog Post
              </button>
            </>
          )}

          {step === 'generating' && (
            <button
              onClick={() => setStep('prompt')}
              className="text-muted-foreground hover:text-foreground ml-auto text-sm"
            >
              Cancel
            </button>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={() => setStep('prompt')}
                className="text-muted-foreground hover:text-foreground text-sm"
              >
                ← Start Over
              </button>
              <div className="flex items-center gap-4">
                {/* Rating for training */}
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">Rate this:</span>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className="p-0.5 transition-transform hover:scale-110"
                      >
                        <Star
                          className={`h-5 w-5 ${
                            star <= rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-muted-foreground/50'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedPost?.content || '');
                    toast.success('Content copied to clipboard');
                  }}
                  className="hover:bg-muted inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm transition-colors"
                >
                  <Copy className="h-4 w-4" />
                  Copy HTML
                </button>
                <button
                  onClick={handleUseGenerated}
                  disabled={savingTraining}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-xl px-6 py-2.5 font-medium transition-colors disabled:opacity-50"
                >
                  {savingTraining ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Use This Content
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
